import React, { useState, useEffect, useRef } from 'react'
import { toast } from 'react-toastify';
import illustration from "../../Assets/illustration.png"
import "./BuildChatbotStep.css"
import { FaLink, FaFileAlt, FaRocket } from "react-icons/fa";
import { useNavigate, useLocation } from 'react-router-dom';


const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

const BuildChatbotStep = () => {
  const [step, setStep] = useState(1);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [user, setUser] = useState(null);
  const [method, setMethod] = useState("url");
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isBuilding, setIsBuilding] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    role: "",
    heardFrom: "",
    deployPlan: "",
    companyName: "",
    employeeCount: "",
    industry: "",
    department: "",
    useCases: [],
    email: "",
    botName: "",
    website: "",
    uploadedFiles: [],
    botAvatarURL: "",
    accentColor: "",
  });


  useEffect(() => {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) return;

    const parsedUser = JSON.parse(storedUser);
    setUser(parsedUser);

    // Always set email in formData
    setFormData((prev) => ({
      ...prev,
      email: parsedUser.email,
    }));

    // Fetch bots (optional check – backend enforces limits anyway)
    const fetchBots = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/auth/get-bots-by-email/${parsedUser.email}`
        );

        if (!res.ok) throw new Error("Failed to fetch bots");

        const bots = await res.json();

        // console.log("Existing bots:", bots.length);
      } catch (error) {
        console.error("Bot fetch error:", error.message);
      }
    };

    fetchBots();

  }, [navigate]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleUseCaseChange = (useCase) => {
    const updated = formData.useCases.includes(useCase)
      ? formData.useCases.filter((item) => item !== useCase)
      : formData.useCases.length < 2
        ? [...formData.useCases, useCase]
        : formData.useCases;
    setFormData({ ...formData, useCases: updated });
  };


  const nextStep = () => {
    let isValid = true;

    if (step === 1) {
      const { fullName, role, heardFrom, deployPlan } = formData;
      if (!fullName || role === "Select one" || heardFrom === "Select one" || deployPlan === "Select one") {
        toast.error("Please complete all fields in Step 1");
        isValid = false;
      }
    }

    if (step === 2) {
      const { companyName, employeeCount, industry, department } = formData;
      if (!companyName || employeeCount === "Select one" || industry === "Select one" || department === "Select one") {
        toast.error("Please complete all fields in Step 2");
        isValid = false;
      }
    }

    if (step === 3) {
      if (formData.useCases.length === 0) {
        toast.error("Please select at least one use case");
        isValid = false;
      }
    }

    if (step === 4) {
      if (!formData.botName) {
        toast.error("Please enter a bot name");
        isValid = false;
      }
    }

    if (step === 5) {
      if (!formData.website && (!formData.uploadedFiles || formData.uploadedFiles.length === 0)) {
        toast.error("Please provide a website URL or upload at least one file");
        isValid = false;
      }
    }

    if (step === 6) {
      if (!formData.accentColor || !formData.botAvatarURL) {
        if (!formData.accentColor) toast.error("Please choose an accent color");
        if (!formData.botAvatarURL) toast.error("Please upload a bot image");
        isValid = false;
      }
    }

    if (isValid) {
      if (step < 6) {
        setStep((prev) => prev + 1);
      } else {
        toast.success("Form submitted successfully!");
        // console.log(formData); For Testing purpose
      }
    }
  };
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));
  const handleSubmit = async () => {
    if (uploadInProgress) {
      toast.error("Image is still uploading. Please wait.");
      return;
    }
    if (!formData.botAvatarURL) {
      toast.error("Please upload a bot image");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please login again");
      return;
    }
    setIsBuilding(true);
    const buildingToastId = toast.loading("🚀 Building your bot... Please wait");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login again");
        return;
      }
      const form = new FormData();
      for (let key in formData) {
        if (key === "useCases") {
          form.append("useCases", JSON.stringify(formData.useCases));
        } else if (
          key !== "uploadedFiles" &&
          key !== "botAvatarURL"
        ) {
          form.append(key, formData[key]);
        }
      }
      form.append("botAvatarURL", formData.botAvatarURL);


      const response = await fetch(`${API_BASE_URL}/api/auth/submit`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: form,
      });
      const result = await response.json();
      if (!response.ok) {
        if (result.code === "BOT_LIMIT_REACHED") {
          toast.error(
            `You’ve reached your bot limit (${result.current}/${result.limit}). Upgrade your plan.`
          );
          navigate("/dashboard");
          return;
        }
        throw new Error(result?.error || "Submit failed");
      }
      const { botId } = result;
      if (!botId) {
        throw new Error("Bot ID not returned from server");
      }

      // Uploading files NodeJS to Python
      if (formData.uploadedFiles?.length) {
        for (let file of formData.uploadedFiles) {
          const fd = new FormData();
          fd.append("file", file);

          const trainRes = await fetch(
            `${API_BASE_URL}/api/auth/bots/${botId}/train-file`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
              },
              body: fd,
            }
          );
          if (!trainRes.ok) {
            const errData = await trainRes.json().catch(() => null);
            if (trainRes.status === 403 && errData?.code === "UPLOAD_LIMIT_REACHED") {
              toast.warning(
                `Bot created successfully, but upload limit reached. Remaining characters: ${errData.remaining}`
              );
              navigate("/dashboard");
              return;
            }
            throw new Error(errData?.error || "File training failed");
          }
        }
      }
      // Uploading links
      if (formData.website) {
        const linkRes = await fetch(
          `${API_BASE_URL}/api/auth/bots/${botId}/train-links`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ links: [formData.website] }),
          }
        );
        if (!linkRes.ok) {
          const errData = await linkRes.json().catch(() => null);

          if (linkRes.status === 403 && errData?.code === "UPLOAD_LIMIT_REACHED") {
            toast.warning(
              `Bot created successfully, but upload limit reached. Remaining characters: ${errData.remaining}`
            );
            navigate("/dashboard");
            return;
          }
          throw new Error(errData?.error || "Link training failed");
        }
      }
      toast.update(buildingToastId, {
        render: "🎉 Your chatbot is ready!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      navigate("/dashboard");
    } catch (err) {
      toast.update(buildingToastId, {
        render: err.message || "Something went wrong",
        type: "error",
        isLoading: false,
        autoClose: 4000,
      });
    }
    finally {
  setIsBuilding(false);
}
  };

  const handleFileUpload = (e) => {
    const uploadedFiles = Array.from(e.target.files).slice(0, 5);
    setFormData({ ...formData, uploadedFiles });
  };

  const accentColors = [
    "#d64545", "#e67e22", "#f1c40f", "#27ae60", "#1abc9c",
    "#3498db", "#2980b9", "#9b59b6", "#e84393",
    "linear-gradient(45deg, red, orange, yellow, green, blue, indigo, violet)"
  ];

  const handleAccentColor = (color) => {
    setFormData({ ...formData, accentColor: color });
  };
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadInProgress(true);

      const cloudName = "duzxrffoe";
      const uploadPreset = "XenaChat";

      const data = new FormData();
      data.append("file", file);
      data.append("upload_preset", uploadPreset);

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        { method: "POST", body: data }
      );

      const result = await res.json();
      if (!result.secure_url) throw new Error("Upload failed");

      setFormData((prev) => ({
        ...prev,
        botAvatarURL: result.secure_url,
      }));

      toast.success("Bot avatar uploaded");
    } catch (err) {
      toast.error("Avatar upload failed");
    } finally {
      setUploadInProgress(false);
    }
  };

  return (
    <>

      <div className="onboarding-wrapper">
        <div className="onboarding-left">
          <div className="step-indicator">
            {step <= 3 ? (
              <>
                <span className={step >= 1 ? "active" : ""}>1 About you</span>
                <span className={step >= 2 ? "active" : ""}>2 About your Company</span>
                <span className={step === 3 ? "active" : ""}>3 Your use cases</span>
              </>
            ) : (
              <>
                <span className={step >= 4 ? "active" : ""}>4 Basic setup</span>
                <span className={step >= 5 ? "active" : ""}>5 Training data</span>
                <span className={step === 6 ? "active" : ""}>6 Customize widget</span>
              </>
            )}
          </div>

          {step === 1 && (
            <>
              <h4 className="step-title">Step 1 of 3</h4>
              <h2>Welcome to XenaChat</h2>
              <p>Let's get to know you better to personalize your experience.</p>
              <label>Full Name</label>
              <input name="fullName" placeholder="John Smith" value={formData.fullName} onChange={handleChange} />
              <label>What best describes your role?</label>
              <select name="role" value={formData.role} onChange={handleChange}>
                <option>Select one</option>
                <option>Customer Support Manager/Lead</option>
                <option>Product Manager</option>
                <option>Business Owner/Founder</option>
                <option>Sales Manager</option>
                <option>Human Resource Manager</option>
                <option>E-commerce Category Manager</option>
                <option>ITSM (IT Service Management) Manager</option>
                <option>Head of IT/IT Director</option>
                <option>Marketing Manager</option>
                <option>Operations Manager</option>
                <option>Other</option>
              </select>
              <label>How did you hear about us?</label>
              <select name="heardFrom" value={formData.heardFrom} onChange={handleChange}>
                <option>Select one</option>
                <option>Search Engine(Google, Bing, etc.)</option>
                <option>Social Media (LinkedIn, Facebook, Twitter, etc.)</option>
                <option>YouTube</option>
                <option>Blogs/Articles</option>
                <option>Email/Newsletter</option>
                <option>Website review or comparison site</option>
                <option>Referred by Colleague/Friend</option>
                <option>Redirected from Chatsonic</option>
                <option>Online Ads</option>
                <option>Conference/Event</option>
                <option>Other</option>
              </select>
              <label>How soon do you plan to deploy your Bot?</label>
              <select name="deployPlan" value={formData.deployPlan} onChange={handleChange}>
                <option>Select one</option>
                <option>Immediately</option>
                <option>Within a month</option>
                <option>Within 3 months</option>
                <option>in the next 6 months</option>
                <option>Not sure yet</option>

              </select>
            </>
          )}

          {step === 2 && (
            <>
              <h4 className="step-title">Step 2 of 3</h4>
              <h2>Tell Us About Your Company</h2>
              <p>Share details about your company to customize your experience and receive tailored recommendations</p>
              <label>Company Name</label>
              <input name="companyName" placeholder="Acme Corp." value={formData.companyName} onChange={handleChange} />
              <label>Select the number of employees</label>
              <select name="employeeCount" value={formData.employeeCount} onChange={handleChange}>
                <option>Select one</option>
                <option>2-25</option>
                <option>25-50</option>
                <option>50-100</option>
                <option>100-250</option>
                <option>250-1000</option>
                <option>1000+</option>
              </select>
              <label>What industry does your company operate in?</label>
              <select name="industry" value={formData.industry} onChange={handleChange}>
                <option>Select one</option>
                <option>Marketing</option>
                <option>E-commerce</option>
                <option>Technology</option>
                <option>Media & Entertainment</option>
                <option>Education</option>
                <option>Healthcare</option>
                <option>Retail</option>
                <option>Finance</option>
                <option>Travel & Hospitaity</option>
                <option>Real Estate</option>
                <option>Other</option>
              </select>
              <label>What department are you in?</label>
              <select name="department" value={formData.department} onChange={handleChange}>
                <option>Select one</option>
                <option>Customer Support</option>
                <option>Customer Success</option>
                <option>Sales</option>
                <option>IT/Technical Support</option>
                <option>Human Resources</option>
                <option>Training & Development</option>
                <option>Operations</option>
                <option>Marketing/Growth</option>
                <option>Communications/PR</option>
                <option>Product Management</option>
                <option>Other</option>
              </select>
            </>
          )}

          {step === 3 && (
            <>
              <h4 className="step-title">Step 3 of 3</h4>
              <h2>How can XenaChat supercharge your work?</h2>
              <p>This will help us drive your success story.</p>
              <p className="select-note">Select up to 2 options</p>

              <div className="usecase-options">
                {[
                  "Handle Customer/IT/HR support tickets",
                  "Improve access to a knowledge base",
                  "Help customers track and place orders",
                  "Employee onboarding and training",
                  "Automate sales inquiries and follow-ups",
                  "Improve customer engagement",
                  "Other",
                ].map((useCase) => (
                  <label key={useCase} className={`usecase-box ${formData.useCases.includes(useCase) ? 'selected' : ''}`}>
                    <span>{useCase}</span>
                    <input
                      type="checkbox"
                      checked={formData.useCases.includes(useCase)}
                      onChange={() => handleUseCaseChange(useCase)}
                    />
                  </label>
                ))}
              </div>

            </>
          )}

          {step === 4 && (
            <div className="step4-wrapper">
              <div className="step4-left">
                <p className="step-count">Step 4 of 6</p>
                <h2>What would you like to name your bot?</h2>
                <p className="subtext">You can always change this later.</p>

                <label htmlFor="botName">Bot Name</label>
                <input
                  id="botName"
                  name="botName"
                  value={formData.botName}
                  onChange={handleChange}
                  placeholder="XenaChat"
                />


              </div>


            </div>
          )}

          {step === 5 && (
            <div className="trainbot-wrapper">
              <p className="trainbot-step-label">Step 5 of 6</p>
              <h2 className="trainbot-heading">Provide Information to Train Your Bot</h2>
              <p className="trainbot-subtitle">
                Start by giving your bot a webpage or some files to kickstart your bot's learning.<br />
                You can add more content later.
              </p>

              <div className="trainbot-methods">
                <button
                  className={`trainbot-method-btn ${method === "url" ? "active" : ""}`}
                  onClick={() => setMethod("url")}
                >
                  <FaLink /> URL
                </button>
                <button
                  className={`trainbot-method-btn ${method === "files" ? "active" : ""}`}
                  onClick={() => setMethod("files")}
                >
                  <FaFileAlt /> Files
                </button>
                <button
                  className={`trainbot-method-btn ${method === "apps" ? "active" : ""}`}
                  onClick={() => setMethod("apps")}
                >
                  <FaRocket /> Apps
                </button>
              </div>

              {method === "url" && (
                <>
                  <label className="trainbot-input-label">Enter your company website</label>
                  <div className="trainbot-input-group">
                    <input
                      type="text"
                      placeholder="www.example.com"
                      value={formData.website || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, website: e.target.value })
                      }
                    />
                    <button className="trainbot-url-btn">
                      <span role="img" aria-label="arrow">🚀</span>
                    </button>
                  </div>
                  <p className="trainbot-note">
                    Don't have a website? <a href="#">Use a sample website</a>
                  </p>
                </>
              )}
              {method === "files" && (
                <>
                  <p className="trainbot-upload-info">
                    Upload up to 5 documents. You can add more documents after onboarding.
                  </p>
                  <label className="trainbot-file-upload">
                    <span className="upload-label">Upload</span> PDF, DOCX file up to 100 MB
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      multiple
                      hidden
                      onChange={handleFileUpload}
                    />
                  </label>
                  {formData.uploadedFiles?.map((file, index) => (
                    <div className="trainbot-file-chip" key={index}>
                      <span className="trainbot-file-icon">📄</span>
                      <span className="trainbot-file-name" title={file.name}>
                        {file.name.length > 30
                          ? file.name.slice(0, 28) + "..."
                          : file.name}
                      </span>
                      <button
                        className="trainbot-remove-btn"
                        onClick={() => {
                          const updated = [...formData.uploadedFiles];
                          updated.splice(index, 1);
                          setFormData({ ...formData, uploadedFiles: updated });
                        }}
                      >
                        ❌
                      </button>
                    </div>
                  ))}

                  {formData.uploadedFiles?.length < 5 && (
                    <label className="trainbot-file-add">
                      + Add
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        hidden
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          const updatedFiles = [
                            ...(formData.uploadedFiles || []),
                            ...files.slice(0, 5 - (formData.uploadedFiles?.length || 0)),
                          ];
                          setFormData({ ...formData, uploadedFiles: updatedFiles });
                        }}
                      />
                    </label>
                  )}
                </>
              )}


              {method === "apps" && (
                <>
                  <div className="trainbot-app-icons">
                    {["notion", "gdrive", "dropbox", "gdocs", "salesforce"].map((app) => (
                      <div key={app} className="trainbot-app-icon">
                        <img src={`/icons/${app}.svg`} alt={app} />
                      </div>
                    ))}
                  </div>
                  <p className="trainbot-app-msg">
                    More data source options become available after onboarding is completed.
                    <br />
                    Please try training your bot via <a href="#">file upload</a> or <a href="#">URLs</a> for now.
                  </p>
                </>
              )}
              {/* You can expand here for file upload or app integrations */}


            </div>
          )}


          {step === 6 && (
            <div className="botcustom-wrapper">
              <p className="botcustom-step">Step 6 of 6</p>
              <h2 className="botcustom-title">Customize Your Bot's Appearance</h2>
              <p className="botcustom-subtext">
                Personalize your bot to match your brand.<br />
                See changes in real-time.
              </p>

              {/* Upload Section */}
              <div className="botcustom-upload-section">
                <div className="botcustom-avatar">
                  {formData.botAvatarURL ? (
                    <img src={formData.botAvatarURL} alt="Bot Avatar" />
                  ) : (
                    <div className="placeholder-img">🤖</div>
                  )}

                </div>
                <div>
                  <label className="botcustom-upload-label">Upload Bot Image</label>
                  <button className="botcustom-upload-btn" onClick={() => fileInputRef.current.click()}>
                    Upload image
                  </button>
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    ref={fileInputRef}
                    onChange={handleAvatarUpload}
                  />
                </div>
              </div>

              {/* Color Picker */}
              <label className="botcustom-color-label">Choose Accent Color</label>
              <div className="botcustom-color-palette">
                {accentColors.map((color, i) => (
                  <div
                    key={i}
                    className={`color-circle ${formData.accentColor === color ? "selected" : ""}`}
                    style={{
                      background: color,
                      border: color.includes("gradient") ? "2px solid #6c4ee3" : undefined,
                    }}
                    onClick={() => handleAccentColor(color)}
                  />
                ))}
              </div>


            </div>
          )

          }

          <div className="step-actions">
            {step > 1 && step <= 6 && <button onClick={prevStep}>Back</button>}

            {step < 6 && (
              <button onClick={nextStep}>Next</button>
            )}

            {step === 6 && (
              <button
                onClick={handleSubmit}
                disabled={uploadInProgress || isBuilding}
              >
                {uploadInProgress
                  ? "Uploading image..."
                  : isBuilding
                    ? "Building your bot..."
                    : "Submit"}
              </button>
            )}
          </div>


        </div>

        <div className="onboarding-right">
          {/* Placeholder for illustration or icons */}
          <img src={illustration} alt="Illustration" className="right-img" />
        </div>
      </div>
    </>
  )
}

export default BuildChatbotStep;